/* eslint-disable max-lines-per-function */
import { createGlobalState } from 'react-use';
import { useCallback } from 'react';

import { VisitId } from '../types';

export type EmergencyNotificationVariant = 'real' | 'test';

type EmergencyNotificationIdleState = {
   type: 'idle';
};

type EmergencyNotificationActiveState = {
   type: 'active';
   variant: EmergencyNotificationVariant;
};

type EmergencyNotificationAcknowledgedState = {
   type: 'acknowledged';
};

export type EmergencyNotificationState =
   | EmergencyNotificationIdleState
   | EmergencyNotificationActiveState
   | EmergencyNotificationAcknowledgedState;

const useEmergencyNotificationGlobalState = createGlobalState<EmergencyNotificationState>({
   type: 'idle',
});

type EmergencyNotificationContext = {
   visitId: VisitId | null;
   notificationId: number | null;
};

type EmergencyNotificationContextState = EmergencyNotificationContext;

const useEmergencyNotificationContextGlobalState = createGlobalState<EmergencyNotificationContextState>({
   visitId: null,
   notificationId: null,
});

type TriggerEmergencyNotification = (variant: EmergencyNotificationVariant) => void;
type CancelEmergencyNotification = VoidFunction;
type SetVisitForEmergencyNotification = (visitId: VisitId | null) => void;
type SetNotificationIdForEmergencyNotification = (notificationId: number | null) => void;
type AcknowledgeEmergencyNotification = (notificationId: number) => void;

type UseEmergencyNotificationReturnFn = {
   state: EmergencyNotificationState;
   context: EmergencyNotificationContext;
   trigger: TriggerEmergencyNotification;
   cancel: CancelEmergencyNotification;
   acknowledge: AcknowledgeEmergencyNotification;
   setVisitForEmergencyNotification: SetVisitForEmergencyNotification;
   setNotificationIdForEmergencyNotification: SetNotificationIdForEmergencyNotification;
};

export const useEmergencyNotification = (): UseEmergencyNotificationReturnFn => {
   const [state, setState] = useEmergencyNotificationGlobalState();
   const [context, setContext] = useEmergencyNotificationContextGlobalState();

   const setVisitForSOS = useCallback<SetVisitForEmergencyNotification>(
      (visitId) => {
         setContext((prevState) => ({
            ...prevState,
            visitId: visitId,
         }));
      },
      [setContext]
   );

   const setNotificationIdForEmergencyNotification = useCallback<SetNotificationIdForEmergencyNotification>(
      (notificationId) => {
         setContext((prevState) => ({
            ...prevState,
            notificationId: notificationId,
         }));
      },
      [setContext]
   );

   const trigger = useCallback<TriggerEmergencyNotification>(
      (variant) => {
         if (state.type === 'idle') {
            setState({
               type: 'active',
               variant: variant,
            });
         }
      },
      [setState, state.type]
   );

   const acknowledge = useCallback<AcknowledgeEmergencyNotification>(
      (notificationId) => {
         if (state.type === 'active' && context.notificationId === notificationId) {
            setState({
               type: 'acknowledged',
            });
         }
      },
      [context.notificationId, state.type, setState]
   );

   const cancel = useCallback(() => {
      setNotificationIdForEmergencyNotification(null);
      setState({
         type: 'idle',
      });
   }, [setState, setNotificationIdForEmergencyNotification]);

   return {
      state: state,
      context: context,
      trigger: trigger,
      cancel: cancel,
      acknowledge: acknowledge,
      setVisitForEmergencyNotification: setVisitForSOS,
      setNotificationIdForEmergencyNotification: setNotificationIdForEmergencyNotification,
   };
};
